import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { setupDayjs } from './date-helpers'
import { SignageNativeConfig } from 'common/types'
import { ShakaErrorCode, VideoPlayerManager } from '@novasign/app-video-player'
import { getShakaErrorCode } from './components/Player/VideoPlayer'

declare var __COMMIT_HASH: string
declare var __APP_VERSION: string

  // --- NativeBridge for Tizen/WebOS cross-origin iframe (no SW support) ---
  // When Service Workers are unavailable, the parent can't inject window.NativeBridge
  // directly. The parent signals via ?platform=tizen URL param. This sets up a
  // postMessage-based bridge that the parent can detect and use.
;(function setupIframeBridge() {
  const params = new URLSearchParams(window.location.search)
  const platform = params.get('platform')

  // Only activate for Tizen/WebOS iframe contexts
  if (!platform || (platform !== 'tizen' && platform !== 'webos')) {
    ;(window as any).__tizenBridgeSkipped = 'platform=' + platform
    return
  }

  // Already injected (SW-based Tizen or Android) — don't override
  if ((window as any).NativeBridge) {
    ;(window as any).__tizenBridgeSkipped = 'already-exists'
    return
  }

  let idCounter = 0
  const pending = new Map<string, (result: string | null) => void>()

  window.addEventListener('message', function handleResponse(e) {
    if (e.data?.type === 'NATIVE_BRIDGE_CALL' && e.data.method === '__ping') {
      // Parent is pinging us to check if bridge is ready
      window.parent.postMessage({ type: 'NATIVE_BRIDGE_CALL', method: '__pong', args: [platform] }, '*')
      return
    }
    if (e.data?.type === 'NATIVE_BRIDGE_RESPONSE' && e.data.id) {
      const resolve = pending.get(e.data.id)
      if (resolve) {
        pending.delete(e.data.id)
        resolve(e.data.result)
      }
    }
  })

  function fireAndForget(method: string, args: string[]): void {
    window.parent.postMessage({ type: 'NATIVE_BRIDGE_CALL', method, args }, '*')
  }

  const ua = navigator.userAgent
  const tizenVersion = (ua.match(/Tizen[ \/]?(\d+\.\d+)/i) || [])[1]
  const chromeVersion = (ua.match(/Chrome\/(\d+)/) || [])[1] || 'unknown'

  ;(window as any).NativeBridge = {
    platform,
    capabilities: ['caching'],
    getConfig() {
      return JSON.stringify({
        hwInfo: {
          os: platform,
          version: chromeVersion,
          appVersion: __APP_VERSION,
          isTV: true,
          isTesting: false,
          brand: 'Samsung',
          model: tizenVersion ? 'Tizen ' + tizenVersion : 'Tizen TV',
          release: tizenVersion || 'unknown',
          diskInfo: { availableBytes: 0, totalBytes: 0 },
        },
      })
    },
    setCacheConf(j: string) {
      fireAndForget('setCacheConf', [j])
    },
    getCacheStats(): string | null {
      return null
    },
    clearCache() {
      fireAndForget('clearCache', [])
    },
    getCacheEntries(): string | null {
      return null
    },
    getCacheTargetsStatus(): string | null {
      return null
    },
    getCacheMisses(): string {
      return '[]'
    },
    setCacheTargets(j: string) {
      fireAndForget('setCacheTargets', [j])
    },
    setScreenInfo(j: string) {
      fireAndForget('setScreenInfo', [j])
    },
  }

  // Signal to parent that bridge is ready
  window.parent.postMessage({ type: 'NATIVE_BRIDGE_CALL', method: '__bridgeReady', args: [platform] }, '*')

  // Debug flag for diagnostics
  ;(window as any).__tizenBridgeReady = true
  ;(window as any).__tizenBridgePlatform = platform
})()

declare global {
  interface Window {
    ReactNativeWebView?: any
    signageNativeConfig?: SignageNativeConfig
    appInfo: { commitHash: string }
    vpm: VideoPlayerManager
  }
}
window.appInfo = { commitHash: __COMMIT_HASH }
window.vpm = VideoPlayerManager.getInstance()

const isRecoverableShakaMediaSourceError = (value: any): boolean => {
  const code = getShakaErrorCode(value)
  return code === ShakaErrorCode.MEDIA_SOURCE_OPERATION_THREW || code === ShakaErrorCode.MEDIA_SOURCE_OPERATION_FAILED
}

window.addEventListener('unhandledrejection', (event) => {
  if (!isRecoverableShakaMediaSourceError(event.reason)) return
  console.warn('Suppressed unhandled rejection for recoverable Shaka MSE error', event.reason)
  event.preventDefault()
})

window.addEventListener('error', (event: ErrorEvent) => {
  if (!isRecoverableShakaMediaSourceError(event.error)) return
  console.warn('Suppressed global error for recoverable Shaka MSE error', event.error)
  event.preventDefault()
})

setupDayjs()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)

reportWebVitals()
